import {
  useState,
  useMemo,
  useEffect,
  memo,
  Fragment,
} from 'react';
import {
  array,
  arrayOf,
  bool,
  element,
  func,
  oneOf,
  shape,
  string,
} from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';
import { red } from '@material-ui/core/colors';

import Breadcrumb from './Breadcrumb';
import Divider from './Divider';

const useStyles = makeStyles((theme) => ({
  root: {},
  action: {
    marginRight: theme.spacing(1),
  },
  actionIcon: {
    marginRight: theme.spacing(1),
  },
}));

function Header(props) {
  const {
    actions,
    backAction,
    breadcrumb,
    breadcrumbPieces,
    tabs,
    title,
    subtitle,
  } = props;
  const classes = useStyles();
  const [openMenu, setOpenMenu] = useState({});
  const [menuValue, setMenuValue] = useState({});
  const [anchorEls, setAnchorEls] = useState({});

  useEffect(() => {
    const initialMenuValue = {};
    actions.forEach((item, index) => {
      if (item.type === 'menu') {
        const selected = item.options.find(({ selected }) => selected);
        if (selected) {
          initialMenuValue[`actions_menu_${index}`] = selected;
        }
      }
    });
    setMenuValue(initialMenuValue);
  }, [actions]);

  const handleMenuOpen = (index, event) => {
    setAnchorEls({
      ...anchorEls,
      [`actions_menu_${index}`]: event.currentTarget,
    });
    setOpenMenu({
      ...openMenu,
      [`actions_menu_${index}`]: true,
    });
  };

  const handleMenuClose = (index) => {
    setOpenMenu({
      ...openMenu,
      [`actions_menu_${index}`]: false,
    });
    setTimeout(() => {
      setAnchorEls((prev) => ({
        ...prev,
        [`actions_menu_${index}`]: null,
      }));
    }, 200);
  };

  const memorizedActions = useMemo(() => {
    return actions.map((item, index) => {
      const {
        processing = false,
        disabled = processing || false,
        tooltip = '',
        placement = 'top',
      } = item;

      const renderButton = () => {
        const commonProps = {
          children: item.type === 'menu'
            ? (item.alwaysShowLabel
              ? item.label
              : menuValue[`actions_menu_${index}`]
                ? menuValue[`actions_menu_${index}`].label
                : item.label)
            : item.label,
          color: item.color || (item.type === 'menu' ? 'primary' : 'secondary'),
          disabled: disabled || processing,
          startIcon: processing && item.type !== 'menu'
            ? (<CircularProgress
              color="secondary"
              size={18}
              style={{
                marginRight: 5,
              }}
            />)
            : item.icon,
          style: {
            marginLeft: 5,
            marginRight: 5,
            marginTop: 5,
            marginBottom: 5,
            display: (item.hidden ? 'none' : undefined),
            ...(item.color === 'error' && !disabled && item.type !== 'menu' ? {
              color: 'white',
              backgroundColor: red[500],
              '&:hover': {
                backgroundColor: red[900],
              },
            } : {}),
          },
          variant: item.variant || (item.type === 'menu' ? 'outlined' : 'contained'),
        };

        if (item.type === 'menu') {
          return (
            <Button
              {...commonProps}
              endIcon={openMenu[`actions_menu_${index}`]
                ? <ExpandLessIcon />
                : <ExpandMoreIcon />}
              onClick={(e) => handleMenuOpen(index, e)}
            />
          );
        } else {
          return (
            <Button
              {...commonProps}
              form={item.form}
              onClick={item.fn}
              type={item.type}
            />
          );
        }
      };

      const button = renderButton();

      if (tooltip && (disabled || item.disabled)) {
        return (
          <Tooltip
            title={tooltip}
            placement={placement}
            key={`button-action-${index}`}
          >
            <span>
              {button}
            </span>
          </Tooltip>
        );
      }

      if (item.type === 'menu') {
        return (
          <Fragment key={`menu-action-${index}`}>
            {button}
            <Menu
              anchorEl={anchorEls[`actions_menu_${index}`]}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              getContentAnchorEl={null}
              keepMounted
              onClose={() => handleMenuClose(index)}
              open={Boolean(openMenu[`actions_menu_${index}`])}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              {item.options.filter((option) => !option.hidden).map((option, i) => (
                <MenuItem
                  disabled={option.disabled}
                  key={i}
                  selected={option.selected}
                  onClick={() => {
                    setMenuValue({
                      ...menuValue,
                      [`actions_menu_${index}`]: option,
                    });
                    handleMenuClose(index);
                    if (typeof option.fn === 'function') {
                      option.fn(option);
                    }
                  }}
                >
                  {option.icon && <ListItemIcon children={option.icon} />}
                  <Typography
                    children={option.label}
                    variant="inherit"
                  />
                </MenuItem>
              ))}
            </Menu>
          </Fragment>
        );
      } else {
        return <div key={`button-action-${index}`}>{button}</div>;
      }
    });
  }, [actions, menuValue, openMenu, anchorEls, classes.menu]);

  return (<>
    <Grid
      className={classes.root}
      container
      justifyContent="space-between"
      spacing={3}
    >
      <Grid item>
        {breadcrumb && <Breadcrumb
          pieces={breadcrumbPieces}
        />}

        <Typography
          color="textPrimary"
          variant="h3"
        >
          {(backAction && !backAction.hidden) && (<>
            {backAction?.label
              ? (<Button
                children={backAction?.label}
                color="primary"
                onClick={backAction?.fn}
                startIcon={backAction?.icon}
              />)
              : (<IconButton
                children={backAction?.icon}
                color="primary"
                onClick={backAction?.fn}
              />)}
          </>)}
          {title}
        </Typography>

        <Typography
          children={subtitle}
          color="textPrimary"
          variant="h6"
        />
      </Grid>

      {!!actions.length && (
        <Grid item>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
          }}>
            {memorizedActions}
          </div>
        </Grid>
      )}
    </Grid>

    {(tabs?.items || []).length > 0 && (<Box mt={2}>
      <Tabs
        onChange={(_, value) => tabs.onChange(value)}
        scrollButtons="auto"
        textColor="secondary"
        value={tabs.value}
        variant="scrollable"
      >
        {(tabs?.items || []).map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            value={tab.value}
          />
        ))}
      </Tabs>
      <Divider />
    </Box>)}
  </>);
}

Header.defaultProps = {
  actions: [],
  breadcrumb: true,
  subtitle: '',
};

Header.propTypes = {
  actions: arrayOf(shape({
    color: oneOf([
      'error',
      'default',
      'primary',
      'secondary',
    ]),
    disabled: bool,
    fn: func,
    form: string,
    icon: element,
    label: string,
    tooltip: string,
    placement: oneOf([
      'top',
      'bottom',
      'left',
      'right',
    ]),
    type: oneOf([
      'button',
      'menu',
      'submit',
    ]),
    variant: oneOf([
      'contained',
      'outlined',
      'text',
    ]),
    alwaysShowLabel: bool,
  })),
  backAction: shape({
    disabled: bool,
    fn: func,
    hidden: bool,
    icon: element,
    label: string,
  }),
  breadcrumb: bool,
  breadcrumbPieces: array,
  tabs: shape({
    items: arrayOf(shape({
      label: string,
      name: string,
    })),
    onChange: func,
    value: string,
  }),
  title: string,
  subtitle: string,
};

export default memo(Header);
